import React, { Fragment, Component, RefObject } from 'react';
import { FormattedMessage, FormattedNumber, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import AutoGrowingInput from './AutoGrowingInput';
import BaseInput from './BaseInput';
import CreditFloorEditInput, { CREDIT_FLOOR_PERCENTAGES } from './CreditFloorEditInput';

import { getCurrencySymbol } from 'businessLogic/server/scsToIcnAdapter';
import {
  getDefaultInvoiceNumber,
  getEditAmountTooltipConfig,
} from 'components/BatchPayments/helpers';
import { ErrorAlert } from 'components/Shared/Icons/Icons';
import CpPopover from 'components/Shared/Popover/CpPopover';
import SegmentIO from 'reporting/SegmentIO';
import { PaymentAmountValidator } from 'shared/PaymentHelpers';
import { calculateCreditFloorAmount } from 'shared/creditFloorHelpers';
import { formatNumberWithCommaAndCurrency } from 'shared/utils';
import { insightSelectors } from 'store/insight/selectors';
import {
  isBatchPaymentsEnabled,
  isPaymentRetryExperimentTreatmentB,
  isTreatmentBCreditFloor,
} from 'store/ixp/selector';
import {
  affirmMaxTxnLimitRawSelector,
  affirmMinTxnLimitRawSelector,
} from 'store/payment/selectors';
import { paymentActions } from 'store/payment/slice';
import { breakpoints, fontSize, colors } from 'styles/cp';
import { UnpaidInvoice } from 'types/Insight';
import { TXN_MAP } from 'types/constants';

// Credit floor percentage constant for TreatmentB (CREDIT_FLOOR_PERCENTAGES imported from CreditFloorEditInput)
const CREDIT_FLOOR_DEFAULT_PERCENTAGE = 0.5;

export type Props = {
  paymentAmountChange: Function;
  paymentAmountReset: Function;
  clearPaymentAmountEditMode: () => void;
  intl: any;
  currency: string;
  balanceAmount: string;
  payment: {
    partialPaymentEnabled?: boolean;
    inputAmount?: string | number;
    defaultInputAmount?: string | number;
    paymentStatus?: any;
    isAmountValid?: boolean;
    paymentMethodType?: string;
    allowOverPay?: boolean;
    isDateScheduled?: boolean;
    isAutoPayOn?: boolean;
    affirmMinTxnLimit?: number;
    affirmMaxTxnLimit?: number;
  };
  featureFlags: any;
  region?: string;
  affirmMinTxnLimit?: number;
  affirmMaxTxnLimit?: number;
  achOnlineConvenienceFeeEnabled?: boolean;
  achOnlineConvenienceFeeAmount?: number;
  ccSurchargingFeePercentage?: string;
  isEstimate?: boolean;
  estimateAmount?: number;
  amountConfig: {
    maxAllowedAmount: number;
    maxEftAllowedAmount: number;
    maxAllowedAmountCcDcACH: number;
    minPaymentAmount: number;
    multipleFactor: number;
  };
  isEditAmountDisabled?: boolean;
  onlyDefaultInvoiceSelected?: boolean;
  selectedBatchInvoices?: UnpaidInvoice[];
  batchUnpaidInvoices?: any;
  sale?: any;
  isBatchPaymentsEnabled?: boolean;
  shouldTriggerAmountEdit?: boolean;
  isPaymentRetryExperimentTreatmentB?: boolean;
  creditFloorDeclineAmount?: number;
  isTreatmentBCreditFloorActive?: boolean;
  onEditModeChange?: (isEditMode: boolean) => void;
};

type State = {
  usePrevAmount: boolean;
  prevAmount: number;
  rawAmount: string | number;
  editMode: boolean;
  touched: boolean;
  timeout?: any | null;
  isCreditTooltipOpen: boolean;
  isSchedulePayTooltip: boolean;
  showTooltip: boolean;
  isOtherSelected: boolean;
};

class PaymentAmountInput extends Component<Props, State> {
  ref: RefObject<HTMLDivElement>;
  isMouseDownInsideContainer: boolean;
  constructor(props: Props) {
    super(props);

    this.state = {
      editMode: false,
      touched: false,
      usePrevAmount: false,
      timeout: null,
      isCreditTooltipOpen: false,
      prevAmount: Number(props.payment.inputAmount || 0),
      rawAmount: props.payment.inputAmount || 0,
      isSchedulePayTooltip: false,
      showTooltip: false,
      isOtherSelected: false,
    };

    this.ref = React.createRef();
    this.isMouseDownInsideContainer = false;
  }

  componentDidMount() {
    // Add click handler to close tooltip when clicking outside
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    // Remove click handler when component unmounts
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  /**
   * Check if an element is inside the credit floor container.
   * Checks both the inner wrapper (this.ref) and the outer credit-floor-edit-pay-wrapper.
   */
  isInsideCreditFloorContainer = (target: HTMLElement | Node | null): boolean => {
    if (!target) return false;
    const isInsideInnerWrapper = this.ref.current?.contains(target) ?? false;
    const isInsideOuterWrapper =
      target instanceof HTMLElement && target.closest('.credit-floor-edit-pay-wrapper') !== null;
    return isInsideInnerWrapper || isInsideOuterWrapper;
  };

  handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const isInsideContainer = this.isInsideCreditFloorContainer(target);

    // Track if mousedown was inside the container (used by onBlur for credit floor edit mode)
    this.isMouseDownInsideContainer = isInsideContainer;

    // For TreatmentB credit floor: close edit mode when clicking outside the container
    // This handles the case where input is already unfocused (no blur event would fire)
    if (this.state.editMode && this.props.isTreatmentBCreditFloorActive && !isInsideContainer) {
      this.setState({
        editMode: false,
        prevAmount: Number(this.state.rawAmount),
        usePrevAmount: false,
      });
    }

    // Close tooltip if user clicks outside of it or selects invoices
    if (this.state.showTooltip) {
      this.setState({ showTooltip: false });
    }
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Listen for credit floor edit trigger - only when prop changes from false to true
    if (!prevProps.shouldTriggerAmountEdit && this.props.shouldTriggerAmountEdit) {
      const { isTreatmentBCreditFloorActive } = this.props;

      // For TreatmentB credit floor: auto-set to default percentage of balance amount
      // For all other cases: keep current rawAmount
      const balanceAmountNum = parseFloat(this.props.balanceAmount) || 0;
      const initialAmount = isTreatmentBCreditFloorActive
        ? calculateCreditFloorAmount(balanceAmountNum, CREDIT_FLOOR_DEFAULT_PERCENTAGE)
        : Number(this.state.rawAmount) || 0;

      // Set all related states together for consistency
      this.setState({
        editMode: true,
        touched: true,
        rawAmount: initialAmount.toFixed(2),
        prevAmount: initialAmount,
        isOtherSelected: false,
      });
      this.scrollToRef();
      this.props.clearPaymentAmountEditMode();
    }

    // For batch payments: exit edit mode when edit becomes disabled
    // This happens when user selects another invoice while editing the default invoice amount
    const { isEditAmountDisabled, isBatchPaymentsEnabled } = this.props;

    if (
      isBatchPaymentsEnabled &&
      this.state.editMode &&
      !prevProps.isEditAmountDisabled &&
      isEditAmountDisabled
    ) {
      // Clear any pending timeout and exit edit mode
      if (this.state.timeout) {
        clearTimeout(this.state.timeout);
      }

      // Reset to the current payment amount from props
      const currentAmount = Number(this.props.payment.inputAmount || 0);
      this.setState({
        editMode: false,
        rawAmount: currentAmount,
        prevAmount: currentAmount,
        timeout: null,
      });
    }

    // Notify parent when edit mode changes
    if (prevState.editMode !== this.state.editMode && this.props.onEditModeChange) {
      this.props.onEditModeChange(this.state.editMode);
    }
  }

  render() {
    const onBlur = (
      e?: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLButtonElement>
    ) => {
      // Skip blur when focus is moving to an element inside the credit floor container
      // (e.g. user clicked percentage buttons). For TreatmentB credit floor only, also skip
      // blur when mousedown was inside the container (fallback for when relatedTarget is null).
      // IMPORTANT: Do NOT use isMouseDownInsideContainer for the non-credit-floor path — it
      // can be stale from a previous click (e.g. "Edit amount") and would incorrectly skip
      // blur when the user presses Escape (programmatic blur, relatedTarget is null).
      const relatedTarget =
        e && 'relatedTarget' in e ? (e.relatedTarget as HTMLElement | null) : null;
      const isInsideViaRelatedTarget = this.isInsideCreditFloorContainer(relatedTarget);

      if (
        isInsideViaRelatedTarget ||
        (this.props.isTreatmentBCreditFloorActive && this.isMouseDownInsideContainer)
      ) {
        // Reset the flag for next interaction
        this.isMouseDownInsideContainer = false;
        return;
      }
      // Always reset the flag when not taking the early return
      this.isMouseDownInsideContainer = false;

      if (this.state.usePrevAmount) {
        this.setState(
          {
            editMode: false,
            rawAmount: this.state.prevAmount,
            usePrevAmount: false,
          },
          () => {
            updateReduxState();
          }
        );
      } else {
        updateReduxState();
        if (
          (isAmountValid && this.props.featureFlags['cp-amount-field-changes-improvement']) ||
          !this.props.featureFlags['cp-amount-field-changes-improvement']
        ) {
          // Track edit amount submitted when user confirms the new amount
          const newAmount = parseFloat(this.state.rawAmount?.toString()) || 0;
          const originalAmount = Number(this.props.payment.defaultInputAmount) || 0;
          if (newAmount !== originalAmount) {
            SegmentIO.editAmountSubmitted(newAmount);
          }

          this.setState({
            editMode: false,
            prevAmount: Number(this.state.rawAmount),
            usePrevAmount: false,
          });
        }
      }
    };

    const isAmountFieldChanges =
      this.props.featureFlags && this.props.featureFlags['cp-amount-field-changes-improvement'];

    const updateReduxState = () => {
      const rawAmount = isNaN(parseFloat(this.state.rawAmount?.toString()))
        ? 0
        : parseFloat(this.state.rawAmount?.toString());
      const { balanceAmount } = this.props;
      this.props.paymentAmountChange(rawAmount, balanceAmount);
    };

    // TreatmentB credit floor: handler for percentage buttons (50%, 75%)
    const onPercentageClick = (percentage: number) => {
      // Track which percentage button was clicked
      const percentageValue = percentage === 0.5 ? '50' : '75';
      SegmentIO.paymentRetryWorkflowAmtBtnClicked(percentageValue);
      const balanceAmountNum = parseFloat(this.props.balanceAmount) || 0;
      if (balanceAmountNum > 0) {
        const newAmount = calculateCreditFloorAmount(balanceAmountNum, percentage);
        // Update both rawAmount and prevAmount for state consistency
        // Format rawAmount with 2 decimal places to ensure cents display correctly (e.g., "50.50" not "50.5")
        this.setState(
          {
            rawAmount: newAmount.toFixed(2),
            prevAmount: newAmount,
            isOtherSelected: false,
          },
          () => {
            updateReduxState();
          }
        );
      }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (isAmountFieldChanges) {
        const inputRegex = /^\d*\.?\d{0,2}$/;
        const valueWithoutCurrency = value.replace(/^[^\d]+/, '');
        const numericValue = valueWithoutCurrency.replace(/,/g, '');

        const isRegexValid = inputRegex.test(numericValue);
        if (isRegexValid) {
          this.setState({
            rawAmount: numericValue,
          });
        } else return;
      } else {
        this.setState({
          rawAmount: e.target.value,
        });
      }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { timeout } = this.state;
      clearTimeout(timeout);
      if (e.key === 'Enter') {
        e.currentTarget.blur();
        return;
      } else if (e.key === 'Escape') {
        if (isAmountFieldChanges) {
          onResetClicked();
        } else {
          const eventBlur = e.currentTarget.blur.bind(e.target); // this will invoke onBlur func
          this.setState(
            {
              usePrevAmount: true,
            },
            () => {
              eventBlur();
            }
          );
        }
        return;
      }

      const currentMethod = this.props.payment?.paymentMethodType;

      // Do not update redux while typing if payment method is BNPL
      if (currentMethod !== 'affirm_bnpl') {
        this.setState({
          timeout: setTimeout(updateReduxState, isAmountFieldChanges ? 50 : 350),
        });
      }
    };
    const isShowTooltip = () => {
      if (isEditAmountDisabled) {
        this.setState({
          showTooltip: !this.state.showTooltip,
        });

        // Auto-hide tooltip after 3 seconds
        if (!this.state.showTooltip) {
          setTimeout(() => {
            this.setState({ showTooltip: false });
          }, 3000);
        }
      }
    };

    const onLabelClick = () => {
      const {
        payment: { partialPaymentEnabled },
        isEditAmountDisabled,
      } = this.props;

      if (isSpOrAp) {
        this.setState({
          isSchedulePayTooltip: true,
        });
        return;
      }
      if (isEditAmountDisabled) {
        return;
      }

      const isPartialPaymentAllowed = partialPaymentEnabled;
      // Partial payment flag that comes from server determines whether we allow the amount to be changed.
      if (!isPartialPaymentAllowed || isPaymentsBlocked() || paymentMethodType === 'nanopay') {
        return;
      }

      // For batch payments only: sync rawAmount with current inputAmount if out of sync
      // This happens when invoice selection changes (e.g., selecting only default after selecting all)
      // Only apply if: 1) feature flag is enabled, 2) isEditAmountDisabled prop exists
      const currentAmount = Number(this.props.payment.inputAmount || 0);
      const { isBatchPaymentsEnabled } = this.props;
      const isBatchPaymentMode = isBatchPaymentsEnabled && isEditAmountDisabled !== undefined;
      const shouldSyncAmount = isBatchPaymentMode && this.state.rawAmount !== currentAmount;

      // Track edit amount selected when user clicks directly on the amount
      // Use payOtherAmountSelected for TreatmentB credit floor case
      if (this.props.isTreatmentBCreditFloorActive) {
        SegmentIO.payOtherAmountSelected();
      } else {
        SegmentIO.editAmountSelected();
      }

      this.setState({
        editMode: true,
        touched: true,
        rawAmount: shouldSyncAmount ? currentAmount : this.state.rawAmount,
        prevAmount: shouldSyncAmount ? currentAmount : this.state.prevAmount,
        timeout: setTimeout(updateReduxState, isAmountFieldChanges ? 50 : 350),
      });
      this.scrollToRef();
    };

    const onOtherClick = () => {
      // Track edit amount selected when user clicks "Other" button
      // Use payOtherAmountSelected for TreatmentB credit floor case
      if (this.props.isTreatmentBCreditFloorActive) {
        SegmentIO.payOtherAmountSelected();
      } else {
        SegmentIO.editAmountSelected();
      }
      // Track "Other" button clicked
      SegmentIO.paymentRetryWorkflowAmtBtnClicked('OTH');
      this.setState(
        {
          editMode: true,
          touched: true,
          rawAmount: this.state.rawAmount,
          prevAmount: this.state.prevAmount,
          isOtherSelected: true,
          timeout: setTimeout(updateReduxState, isAmountFieldChanges ? 50 : 350),
        },
        () => {
          // Focus and select the input text after state update
          const input = this.ref.current?.querySelector('input');
          if (input) {
            input.focus();
            input.select();
          }
        }
      );
    };

    const onResetClicked = (e?: React.MouseEvent) => {
      e?.stopPropagation();
      e?.nativeEvent.stopImmediatePropagation();
      // Track reset button clicked in credit floor workflow
      if (isTreatmentBCreditFloorEditMode) {
        SegmentIO.paymentRetryWorkflowResetClicked();
      }
      this.props.paymentAmountReset();
      this.setState({
        editMode: false,
        touched: false,
        prevAmount: Number(this.props.payment.defaultInputAmount),
        rawAmount: this.props.payment.defaultInputAmount || 0,
        isOtherSelected: false,
      });
    };

    const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      const select = e.currentTarget.select.bind(e.target);
      this.setState(
        {
          editMode: true,
          touched: true,
          timeout: setTimeout(updateReduxState, isAmountFieldChanges ? 50 : 350),
        },
        () => {
          select();
        }
      );
    };

    const maybeShowOutstandingBalance = () => {
      const {
        currency,
        balanceAmount,
        payment: { inputAmount },
        achOnlineConvenienceFeeAmount = 0,
      } = this.props;

      const outstandingBalance = parseFloat(balanceAmount) - parseFloat(String(inputAmount));
      if (outstandingBalance > 0) {
        return (
          <Fragment>
            <div className="balance-due">
              <span className="balance-due-message">
                <FormattedMessage
                  id="INVOICE_SUMMARY_INVOICE_OUTSTANDING_BALANCE"
                  defaultMessage="Outstanding balance"
                />
                :&nbsp;
              </span>
              <span className="currency-value">
                <FormattedNumber style="currency" currency={currency} value={outstandingBalance} />
              </span>
            </div>
          </Fragment>
        );
      } else if (!achOnlineConvenienceFeeAmount && outstandingBalance < 0) {
        return (
          <Fragment>
            <div className="balance-due">
              <span
                className="balance-due-message"
                onMouseEnter={() =>
                  this.setState({
                    isCreditTooltipOpen: true,
                  })
                }
                onMouseLeave={() =>
                  this.setState({
                    isCreditTooltipOpen: false,
                  })
                }
              >
                <span id="balance-due-credit" style={{ borderBottom: '2px dotted' }}>
                  <FormattedMessage id="payflow.creditsLabel" defaultMessage="Credit" />{' '}
                </span>
                :
                <span className="balance-due-credit-amount" style={{ color: colors.green03 }}>
                  &nbsp;+
                  <FormattedNumber
                    style="currency"
                    currency={currency}
                    value={Math.abs(outstandingBalance)}
                  />
                </span>
              </span>
              <CpPopover
                className="cp-tooltip-wrapper"
                innerClassName="cp-tooltip"
                placement="bottom-start"
                isOpen={this.state.isCreditTooltipOpen}
                target="balance-due-credit"
              >
                <FormattedMessage
                  id="payflow.credit_amount_message"
                  defaultMessage="A credit will be issued to your account because you׳re overpaying your invoice"
                />
              </CpPopover>
            </div>
          </Fragment>
        );
      }
      return null;
    };

    const isPaymentsBlocked = () => {
      const { featureFlags } = this.props;
      return featureFlags && featureFlags['block-payments'];
    };

    const isEditButtonBlocked = () => {
      const { payment = { paymentMethodType: '' } } = this.props;
      return payment && payment.paymentMethodType === 'nanopay';
    };

    const updateIsTooltipOpen = () => {
      if (isSpOrAp) {
        this.setState({
          isSchedulePayTooltip: !this.state.isSchedulePayTooltip,
        });
      }
    };

    const {
      intl,
      currency,
      balanceAmount,
      payment: {
        inputAmount,
        defaultInputAmount,
        paymentStatus,
        isAmountValid,
        paymentMethodType,
        allowOverPay,
        partialPaymentEnabled,
        isDateScheduled,
        isAutoPayOn,
      },
      region,
      featureFlags,
      achOnlineConvenienceFeeAmount,
      achOnlineConvenienceFeeEnabled,
      isEstimate,
      estimateAmount,
      amountConfig,
      ccSurchargingFeePercentage,
      isEditAmountDisabled,
    } = this.props;
    const isSpOrAp = isDateScheduled || isAutoPayOn;
    const isPartialPaymentAllowed = partialPaymentEnabled;
    let displayAmount;

    if (!this.state.editMode) {
      let amount = Number(String(this.props.payment.inputAmount));
      amount = isNaN(amount) ? 0 : amount;
      displayAmount = intl.formatNumber(amount, { style: 'currency', currency });
    } else {
      displayAmount = this.state.rawAmount;
    }

    // Get dynamic tooltip message for disabled edit amount in batch payments
    const getEditAmountTooltipMessage = () => {
      const { selectedBatchInvoices = [], batchUnpaidInvoices, sale } = this.props;

      if (!this.props.isBatchPaymentsEnabled || !isEditAmountDisabled) {
        return null;
      }

      const defaultInvoiceNumber = getDefaultInvoiceNumber(batchUnpaidInvoices, sale);
      const tooltipConfig = getEditAmountTooltipConfig(selectedBatchInvoices, defaultInvoiceNumber);

      if (!tooltipConfig) return null;

      return intl.formatMessage(
        {
          id: tooltipConfig.id,
          defaultMessage: tooltipConfig.defaultMessage,
        },
        tooltipConfig.values
      );
    };

    // TreatmentB credit floor: check if we should show the wrapped edit mode
    const { isTreatmentBCreditFloorActive: isInCreditFloorTreatment } = this.props;

    const editButton = (
      <span
        className="amount-label-icon"
        id="loadable-edit-icon"
        onFocus={onLabelClick}
        onClick={onLabelClick}
        onTouchStart={onLabelClick}
        {...(isEditAmountDisabled && { style: { position: 'relative' } })}
      >
        <CpPopover
          className="cp-tooltip-wrapper"
          innerClassName="cp-tooltip"
          placement={`top${isEditAmountDisabled ? '' : '-end'}`}
          isOpen={
            (isSpOrAp && this.state.isSchedulePayTooltip) ||
            (isEditAmountDisabled && this.state.showTooltip)
          }
          target="edit"
          showArrow={isEditAmountDisabled}
          {...(isEditAmountDisabled && {
            modifiers: {
              offset: {
                enabled: true,
                offset: '0, 12px',
              },
            },
          })}
        >
          {isEditAmountDisabled && this.props.isBatchPaymentsEnabled ? (
            getEditAmountTooltipMessage()
          ) : (
            <FormattedMessage
              id={
                isEditAmountDisabled
                  ? 'EDIT_AMOUNT_DISABLED_BATCH'
                  : 'SCHEDULE_PAY_EDIT_AMOUNT_DISABLED'
              }
              description="payment amount edit disabled reason"
              defaultMessage={
                isEditAmountDisabled
                  ? 'You can only edit the amount on single invoices.'
                  : "You have to pay in full if you schedule a payment date other than today. You can't change the amount."
              }
            />
          )}
        </CpPopover>
        <button
          id="edit"
          className="input-btn-text edit"
          disabled={isSpOrAp || isEditAmountDisabled}
        >
          {isInCreditFloorTreatment ? (
            <FormattedMessage
              id="PAYFLOW_CREDIT_FLOOR_EDIT_AMOUNT"
              defaultMessage="Pay another amount"
            />
          ) : (
            <FormattedMessage id="PAYFLOW_EDIT_AMOUNT" defaultMessage="Edit amount" />
          )}
        </button>

        {isEditAmountDisabled && (
          <div
            onClick={isShowTooltip}
            style={{
              position: 'absolute',
              inset: 0,
            }}
          />
        )}
      </span>
    );

    const resetButton = (
      <span className="amount-label-icon" id="loadable-reset-icon" onClick={onResetClicked}>
        <CpPopover
          className="cp-tooltip-wrapper"
          innerClassName="cp-tooltip"
          placement="top-end"
          isOpen={this.state.isSchedulePayTooltip}
          target="reset"
        >
          <FormattedMessage
            id="SCHEDULE_PAY_EDIT_AMOUNT_DISABLED"
            description="payment amount edit disabled reason"
            defaultMessage="You have to pay in full if you schedule a payment date other than today. You can’t change the amount."
          />
        </CpPopover>
        <button id="reset" className="input-btn-text" onClick={onResetClicked}>
          <FormattedMessage id="PAYFLOW_RESET" defaultMessage="Reset" />
        </button>
      </span>
    );

    const renderButtonInEditMode = ({ buttonName }: { buttonName: 'save' | 'reset' }) => (
      <button
        id={buttonName}
        className="input-btn-text edit-mode"
        onClick={buttonName === 'save' ? onBlur : onResetClicked}
      >
        <FormattedMessage
          id={buttonName === 'save' ? 'PAYFLOW_SAVE' : 'PAYFLOW_RESET'}
          defaultMessage={buttonName === 'save' ? 'Save' : 'Reset'}
        />
      </button>
    );

    // For batch payments (isEditAmountDisabled), always show edit button, not reset button
    // Only apply if feature flag is enabled
    const showEditButton = this.props.isBatchPaymentsEnabled && isEditAmountDisabled;
    const actionButton =
      !showEditButton &&
      defaultInputAmount &&
      inputAmount !== defaultInputAmount &&
      !isInCreditFloorTreatment
        ? resetButton
        : editButton;

    // We need this function to enable the usage of decimal number
    const formattedAmount =
      isAmountFieldChanges &&
      formatNumberWithCommaAndCurrency({
        value: this.state.rawAmount,
        currency: getCurrencySymbol(currency),
      });

    const cleanValue = isAmountFieldChanges && formattedAmount.replace(/^[^\d]+/, '');

    const { affirmMinTxnLimit, affirmMaxTxnLimit } = this.props;

    const minAmount = PaymentAmountValidator.getMinPaymentAmount(
      balanceAmount,
      paymentMethodType,
      isEstimate,
      amountConfig,
      affirmMinTxnLimit
    );

    const maxAmount = isEstimate
      ? estimateAmount
      : PaymentAmountValidator.getMaxPaymentAmount(
          balanceAmount,
          paymentMethodType,
          allowOverPay,
          region,
          featureFlags,
          amountConfig,
          affirmMaxTxnLimit
        );

    let shouldShowRangeError = !isAmountValid;

    // Hide range message when min/max are invalid (e.g. preview or BNPL limits not loaded - would show "NAN")
    const hasValidRangeAmounts =
      Number.isFinite(Number(minAmount)) && Number.isFinite(Number(maxAmount));

    const formattedFeeAmount = Number(achOnlineConvenienceFeeAmount).toFixed(2);

    const shouldShowCCConvenienceFeeNote = typeof ccSurchargingFeePercentage === 'string';

    const shouldShowACHConvenienceFeeNote =
      achOnlineConvenienceFeeEnabled &&
      Number(achOnlineConvenienceFeeAmount) > 0 &&
      !shouldShowCCConvenienceFeeNote;

    const isTreatmentBCreditFloorEditMode = this.state.editMode && isInCreditFloorTreatment;

    // Calculate percentage amounts for active state detection (based on balanceAmount)
    const balanceAmountNum = parseFloat(balanceAmount) || 0;
    const fiftyPercentAmount = calculateCreditFloorAmount(
      balanceAmountNum,
      CREDIT_FLOOR_PERCENTAGES[0]
    );
    const seventyFivePercentAmount = calculateCreditFloorAmount(
      balanceAmountNum,
      CREDIT_FLOOR_PERCENTAGES[1]
    );
    const currentRawAmount = parseFloat(this.state.rawAmount?.toString()) || 0;

    return (
      <div className="payment-amount-input-wrapper" ref={this.ref}>
        {/* TreatmentB credit floor edit mode */}
        {isTreatmentBCreditFloorEditMode && (
          <CreditFloorEditInput
            value={String(this.state.rawAmount)}
            onChange={onChange}
            onKeyDown={handleKeyPress}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={
              paymentStatus === TXN_MAP.STATUS.IN_PROGRESS ||
              !isPartialPaymentAllowed ||
              isPaymentsBlocked() ||
              isEditButtonBlocked()
            }
            currencySymbol={getCurrencySymbol(currency)}
            currentAmount={currentRawAmount}
            fiftyPercentAmount={fiftyPercentAmount}
            seventyFivePercentAmount={seventyFivePercentAmount}
            isOtherSelected={this.state.isOtherSelected}
            onPercentageClick={onPercentageClick}
            onOtherClick={onOtherClick}
            onResetClick={onResetClicked}
          />
        )}
        {/* Regular edit mode (not treatmentB credit floor) */}
        {this.state.editMode && !isTreatmentBCreditFloorEditMode && isAmountFieldChanges && (
          <div className="edit-payment-amount-wrapper">
            <div className="edit-payment-amount-input">
              <AutoGrowingInput
                inputMode="decimal"
                type="text"
                maxLength={16}
                value={formattedAmount}
                cleanValue={cleanValue}
                isError={!isAmountValid}
                onChange={onChange}
                /** Added setTimeout because of the reset button in edit mode - its going to the onBlur automatically instead (resource: https://github.com/facebook/react/issues/4210) **/
                onBlur={() => setTimeout(onBlur, 500)}
                onFocus={onFocus}
                autoFocus
                onKeyDown={handleKeyPress}
                disabled={
                  paymentStatus === TXN_MAP.STATUS.IN_PROGRESS ||
                  !isPartialPaymentAllowed ||
                  isPaymentsBlocked() ||
                  isEditButtonBlocked()
                }
              />
            </div>
            <>
              <div className="amount-label-icon input-btn">
                {renderButtonInEditMode({ buttonName: 'save' })}
              </div>
              <div className="amount-label-icon input-btn">
                {renderButtonInEditMode({ buttonName: 'reset' })}
              </div>
            </>
          </div>
        )}
        {this.state.editMode && !isTreatmentBCreditFloorEditMode && !isAmountFieldChanges && (
          <BaseInput
            type="text"
            inputMode="decimal"
            value={String(this.state.rawAmount)}
            onKeyDown={handleKeyPress}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            pattern="[0-9]*[.]?[0-9]{0,2}"
            autoFocus
            disabled={
              paymentStatus === TXN_MAP.STATUS.IN_PROGRESS ||
              !isPartialPaymentAllowed ||
              isPaymentsBlocked() ||
              isEditButtonBlocked()
            }
          />
        )}
        {!this.state.editMode && (
          <div
            className="amount-label-icon input-btn"
            onMouseEnter={updateIsTooltipOpen}
            onMouseLeave={updateIsTooltipOpen}
          >
            <label
              className="display-amount"
              onFocus={onLabelClick}
              onClick={onLabelClick}
              onTouchStart={onLabelClick}
              data-testid="sale-display-amount"
            >
              {displayAmount}
            </label>
            {(isPartialPaymentAllowed ||
              (this.props.isBatchPaymentsEnabled && isEditAmountDisabled)) &&
              !isPaymentsBlocked() &&
              !isEditButtonBlocked() &&
              actionButton}
          </div>
        )}
        {shouldShowRangeError &&
          hasValidRangeAmounts &&
          !(this.props.isBatchPaymentsEnabled && !this.props.onlyDefaultInvoiceSelected) && (
            <span className="error-message-wrapper">
              <ErrorAlert />
              &nbsp;
              <span className="error-message payment-amount-message">
                <FormattedMessage
                  id="PAYFLOW_PAYMENT_RANGE_AMOUNT"
                  values={{
                    0: <FormattedNumber style="currency" currency={currency} value={minAmount} />,
                    1: <FormattedNumber style="currency" currency={currency} value={maxAmount} />,
                  }}
                  defaultMessage="Invalid Field"
                />
              </span>
            </span>
          )}
        <div className="payment-data">
          <span>{maybeShowOutstandingBalance()}</span>
        </div>
        {shouldShowACHConvenienceFeeNote && (
          <span className="conveience-fee-note">
            <FormattedMessage
              id="ACH_CONVEIENCE_FEE_NOTE"
              defaultMessage="Includes a ${0} convenience fee"
              values={{
                0: formattedFeeAmount,
              }}
            />
          </span>
        )}
        {shouldShowCCConvenienceFeeNote && (
          <span className="conveience-fee-note">
            <FormattedMessage
              id="CC_CONVEIENCE_FEE_NOTE"
              defaultMessage="Includes a {0}% credit card surcharge"
              values={{
                0: ccSurchargingFeePercentage,
              }}
            />
          </span>
        )}
        {/* language=scss */}
        <style jsx>{`
          :global(.balance-due-message) {
            color: ${colors.gray02};
            font-size: ${fontSize.xs};
            font-family: AvenirNextforINTUIT-Regular;
          }

          :global(.balance-due-credit-amount) {
            font-family: AvenirNextforINTUIT-Demi;
          }

          :global(.currency-value) {
            font-size: ${fontSize.xs};
            color: ${colors.gray02};
            font-family: AvenirNextforINTUIT-Medium;
          }

          .payment-amount-message {
            font-style: normal;
            color: ${colors.darkError};
          }

          .payment-amount-input-wrapper {
            :global(input) {
              padding: 0;
              margin-bottom: 2px;
              font-size: ${fontSize.xl};
              font-family: AvenirNextforINTUIT-Demi;
              &::-webkit-outer-spin-button,
              &::-webkit-inner-spin-button {
                appearance: none;
                margin: 0;
              }
            }
          }

          .edit-payment-amount-wrapper {
            display: flex;
            align-items: flex-end;
            gap: 8px;
          }

          .edit-payment-amount-input {
            display: flex;
            flex-direction: row;
            gap: 4px;
            align-items: center;
            font-size: ${fontSize.xl};
            font-family: AvenirNextforINTUIT-Demi;
            height: 39px;
          }

          .input-btn {
            padding: 0;
            border: none;
            background-color: inherit;

            :global(.input-btn-text) {
              cursor: pointer;
              color: ${colors.gray03};
              font-family: inherit;
              background-color: inherit;
              border: none;
              text-decoration: underline;
              white-space: nowrap;
              -webkit-font-smoothing: antialiased;
            }

            :global(.edit-mode) {
              text-underline-offset: 2px;
              padding: 0;
              margin: 0;
            }
          }

          :global(.amount-label-icon) {
            display: flex;
            align-items: baseline;
          }

          .display-amount {
            cursor: ${isPartialPaymentAllowed ||
            (this.props.isBatchPaymentsEnabled && !isEditAmountDisabled)
              ? 'pointer'
              : ''};
            display: block;
            position: relative;
            line-height: 39px;
            font-size: ${fontSize.xl};
            font-family: AvenirNextforINTUIT-Demi;
            @media screen and (max-width: ${breakpoints.md}) {
              font-size: ${fontSize.lg};
            }
          }

          .payment-data {
            display: none;

            @media screen and (max-width: ${breakpoints.md}) {
              display: flex;
              justify-content: end;
            }
          }

          .conveience-fee-note {
            font-size: ${fontSize.xs};
            font-family: inherit;
            display: flex;
            margin-top: 4px;
            color: ${colors.gray02};
          }
        `}</style>
      </div>
    );
  }

  scrollToRef = () => {
    if (window.innerWidth <= 768 && this.ref.current) {
      window.scrollTo({ behavior: 'smooth', top: this.ref.current.offsetTop - 25 });
    }
  };
}

// @ts-ignore
const mapStateToProps = (state) => {
  const { insight, config, sale, payment } = state;
  const selectedBatchInvoices = insightSelectors.selectedBatchInvoices(insight) || [];
  const batchUnpaidInvoices = insightSelectors.batchUnpaidInvoices(insight);
  const isBatchEnabled = isBatchPaymentsEnabled(state);
  const onlyDefaultInvoiceSelected =
    isBatchEnabled &&
    selectedBatchInvoices?.length === 1 &&
    selectedBatchInvoices[0]?.defaultInvoice;

  return {
    isBatchPaymentsEnabled: isBatchEnabled,
    amountConfig: config?.amountConfig,
    isEditAmountDisabled: sale?.isEditAmountDisabled,
    onlyDefaultInvoiceSelected,
    selectedBatchInvoices: selectedBatchInvoices as UnpaidInvoice[],
    batchUnpaidInvoices,
    sale,
    shouldTriggerAmountEdit: payment?.shouldTriggerAmountEdit,
    isPaymentRetryExperimentTreatmentB: isPaymentRetryExperimentTreatmentB(state),
    creditFloorDeclineAmount: payment?.creditFloorDeclineAmount,
    isTreatmentBCreditFloorActive: isTreatmentBCreditFloor(state),
    affirmMinTxnLimit: affirmMinTxnLimitRawSelector(state),
    affirmMaxTxnLimit: affirmMaxTxnLimitRawSelector(state),
  };
};

// @ts-ignore
const mapDispatchToProps = (dispatch) => ({
  clearPaymentAmountEditMode: () => dispatch(paymentActions.setPaymentAmountEditMode(false)),
});

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(PaymentAmountInput));
